import { Action } from 'redux';
import {
    EditorColor,
    EditorSaveData,
    EditorSendTestData,
    EditorTemplateStatus,
    EditorTemplateVersion,
} from '@dev/template-editor';
import { Template } from '@dev/ee-api/ee-api-models';
import { AvailableFeaturesUpselling } from '@dev/ee-modules';
import { ApplicationState } from '..';

export const emailLimitBackendErrors = [
    'You have used all your free daily email credits. Upgrade your account to an unlimited billing plan to continue sending email.',
    'You can not send above your plan limit. Upgrade your account to a higher tier to continue sending emails.',
    'Error: Daily limit exceeded.',
    'Your plan expired. Renew your account\u0027s plan to continue sending email.',
];

export enum BackendErrors {
    TEMPLATE_SIZE_EXCEEDS_LIMIT_SEND_TEST = 'Allowed email body size limit exceeded (maximum size is {{0}} MB)',
}

export enum SendTestErrorNames {
    OTHER_BACKEND_ERROR = 'OtherBackendError',
    TEMPLATE_SIZE = 'EmailExceedsAllowedSizeLimit',
    INVALID_EMAILS = 'SendTestEmailsInvalid',
    INVALID_EMAILS_STATUS = 'SendTestEmailsStatusInvalid',
    NO_EMAIL_CREDITS = 'NoEmailCredits',
}

export interface EditorMetaData {
    data: {
        contentJson: EditorTemplateVersion;
        recentColors: EditorColor[];
    };
}

export type EditorMetaDataSimple = EditorTemplateVersion;

export type SendTestErrors = Record<string, boolean>;
export interface SendTestData {
    editorData?: EditorSendTestData;
    toEmails: string;
    fromEmail: string;
    fromName: string;
    subject: string;
    previewText?: string;
    errors?: SendTestErrors;
}

export enum EditorMediaManagerTabIndex {
    IMAGE = 0,
    FILE = 1,
}

export type EditorMediaManagerProps = {
    showImageTabOnly: boolean;
    isLinkToFile: boolean;
    blockId: string;
    isOpen: boolean;
};

export type EditorFile = {
    fileUrl: string;
    fileName: string;
    blockId: string;
    isLinkToFile?: boolean;
};

export type FilesType = {
    filesList: EditorFile[];
    isFileUploading: boolean;
};

export type TemplatePreviewType = {
    templateHtml: string;
};

export interface SendTestAction extends Action {
    type: TemplateEditorActionTypes.SEND_TEST;
}
export interface CloseAction extends Action {
    type: TemplateEditorActionTypes.CLOSE;
}

export interface SetSendTestDataAction extends Action {
    type: TemplateEditorActionTypes.SET_SEND_TEST_DATA;
    payload: SendTestData;
}

export interface UploadEditorFileAction extends Action {
    type: TemplateEditorActionTypes.UPLOAD_FILE;
    payload: {
        file: File;
        blockId: string;
        isLinkToFile?: boolean;
    };
}

export interface UploadEditorFileStartAction extends Action {
    type: TemplateEditorActionTypes.UPLOAD_FILE_START;
}

export interface UploadEditorFileSuccessAction extends Action {
    type: TemplateEditorActionTypes.UPLOAD_FILE_SUCCESS;
    payload: {
        filesList: EditorFile[];
    };
}

export interface UploadEditorFileErrorAction extends Action {
    type: TemplateEditorActionTypes.UPLOAD_FILE_ERROR;
}

export interface SetBodyPartAction extends Action {
    type: TemplateEditorActionTypes.SET_BODY_PART;
    payload: {
        contentType: string;
        content: string;
    };
}

export interface DefaultOptions {
    fromEmail?: string;
    fromName?: string;
    subject?: string;
}
export interface SetDefaultOptionsAction extends Action {
    type: TemplateEditorActionTypes.SET_DEFAULT_OPTIONS;
    payload: DefaultOptions;
}

export interface AddTagAction extends Action {
    type: TemplateEditorActionTypes.ADD_TAG;
    payload: {
        tag: string;
    };
}

export interface RemoveTagAction extends Action {
    type: TemplateEditorActionTypes.REMOVE_TAG;
    payload: {
        tag: string;
    };
}

export interface SetDefaultTemplateAction extends Action {
    type: TemplateEditorActionTypes.SET_DEFAULT_TEMPLATE;
    payload: {
        templateObject: Template;
    };
}

export interface LoadStartAction extends Action {
    type: TemplateEditorActionTypes.LOAD_START;
    payload: {
        name: string;
        isPublic: boolean;
        useDraftScope: boolean;
    };
}

export interface LoadSuccessAction extends Action {
    type: TemplateEditorActionTypes.LOAD_SUCCESS;
    payload: {
        templateObject: Template;
        isPublic: boolean;
        useDraftScope: boolean;
    };
}

export interface LoadErrorAction extends Action {
    type: TemplateEditorActionTypes.LOAD_ERROR;
}

export interface IncrementAutosaveCounterAction extends Action {
    type: TemplateEditorActionTypes.INCREMENT_AUTOSAVE_COUNTER;
}

export interface SaveStartAction extends Action {
    type: TemplateEditorActionTypes.SAVE_START;
    payload: {
        isDraftTemplate: boolean;
        useIndividualPaths: boolean;
        data?: EditorSaveData;
        exitOnSave?: boolean;
        onSaveAndExitCallback?: (templateName: string, templateObject: Template) => void;
    };
}

export interface SaveSuccessAction extends Action {
    type: TemplateEditorActionTypes.SAVE_SUCCESS;
}

export interface SaveErrorAction extends Action {
    type: TemplateEditorActionTypes.SAVE_ERROR;
    payload: {
        nameError?: string;
    };
}

export interface ResetAction extends Action {
    type: TemplateEditorActionTypes.RESET;
}

export interface SetTemplatePreviewHtmlAction extends Action {
    type: TemplateEditorActionTypes.SET_TEMPLATE_PREVIEW_HTML;
    payload: {
        templateHtml: string;
    };
}
export interface OpenUpsellingOverlayAction extends Action {
    type: TemplateEditorActionTypes.OPEN_UPSELLING_OVERLAY;
    payload: {
        feature: AvailableFeaturesUpselling;
    };
}
export interface CloseUpsellingOverlayAction extends Action {
    type: TemplateEditorActionTypes.CLOSE_UPSELLING_OVERLAY;
}

export interface TemplateEditorState {
    templateLoadedJson?: EditorTemplateVersion;
    status: EditorTemplateStatus;
    templateName?: string;
    templateObject?: Template;
    isNewTemplate?: boolean;
    isCampaignCreator?: boolean;
    autosaveCounter: number;
    sendTest: SendTestData;
    files: FilesType;
    templatePreview: TemplatePreviewType;
    recentColors: EditorColor[];
    nameError?: string;
    openUpsellingFeature?: AvailableFeaturesUpselling;
    // TO DO: import from new beta editor
    // contactListsOptions?: EditorContactListsOptions;
    contactListsOptions?: any;
}

export enum TemplateEditorActionTypes {
    SEND_TEST = '@@templateeditor/SEND_TEST',
    SET_SEND_TEST_DATA = '@@templateeditor/SET_SEND_TEST_DATA',
    CLOSE = '@@templateeditor/CLOSE',
    UPLOAD_FILE = '@@templateeditor/UPLOAD_FILE',
    UPLOAD_FILE_START = '@@templateeditor/UPLOAD_FILE_START',
    UPLOAD_FILE_SUCCESS = '@@templateeditor/UPLOAD_FILE_SUCCESS',
    UPLOAD_FILE_ERROR = '@@templateeditor/UPLOAD_FILE_ERROR',
    SET_BODY_PART = '@@templateeditor/SET_BODY_PART',
    SET_DEFAULT_OPTIONS = '@@templateeditor/SET_DEFAULT_OPTIONS',
    ADD_TAG = '@@templateeditor/ADD_TAG',
    REMOVE_TAG = '@@templateeditor/REMOVE_TAG',
    SET_DEFAULT_TEMPLATE = '@@templateeditor/SET_DEFAULT_TEMPLATE',
    LOAD_START = '@@templateeditor/LOAD_START',
    LOAD_SUCCESS = '@@templateeditor/LOAD_SUCCESS',
    LOAD_ERROR = '@@templateeditor/LOAD_ERROR',
    INCREMENT_AUTOSAVE_COUNTER = '@@templateeditor/INCREMENT_AUTOSAVE_COUNTER',
    SAVE_START = '@@templateeditor/SAVE_START',
    SAVE_SUCCESS = '@@templateeditor/SAVE_SUCCESS',
    SAVE_ERROR = '@@templateeditor/SAVE_ERROR',
    RESET = '@@templateeditor/RESET',
    SET_TEMPLATE_PREVIEW_HTML = '@@templateeditor/SET_TEMPLATE_PREVIEW_HTML',
    OPEN_UPSELLING_OVERLAY = '@@templateeditor/OPEN_UPSELLING_OVERLAY',
    CLOSE_UPSELLING_OVERLAY = '@@templateeditor/CLOSE_UPSELLING_OVERLAY',
}

export const getTemplateEditor = (store: ApplicationState): TemplateEditorState => store.templateEditor;
